import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BookingForm } from '../components/BookingForm';
import { QuickQuote } from '../components/QuickQuote';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Plane, Map, Users, Compass, CheckCircle, Star, Crown,
  Shield, Clock, Award, ChevronRight, Phone, Mail, MessageCircle,
  CreditCard, Landmark, Smartphone, Banknote, Link2, Handshake, Globe, Route, MapPin
} from 'lucide-react';
import { company, services, serviceTiers, vehicles, tours, longTrips, testimonials, pricing, paymentMethods } from '../mock';
import { Toaster } from '../components/ui/sonner';

export const LandingPage = () => {
  const [language, setLanguage] = useState('en');
  const t = (en, pt) => (language === 'en' ? en : pt);

  const getIcon = (iconName) => {
    const icons = {
      plane: <Plane className="w-7 h-7" />,
      map: <Map className="w-7 h-7" />,
      users: <Users className="w-7 h-7" />,
      compass: <Compass className="w-7 h-7" />
    };
    return icons[iconName] || <Star className="w-7 h-7" />;
  };

  const payIcon = (icon) => {
    const map = {
      'credit-card': <CreditCard className="w-5 h-5" />,
      'bank': <Landmark className="w-5 h-5" />,
      'smartphone': <Smartphone className="w-5 h-5" />,
      'banknote': <Banknote className="w-5 h-5" />,
      'link': <Link2 className="w-5 h-5" />
    };
    return map[icon] || <CreditCard className="w-5 h-5" />;
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Toaster position="top-center" />
      <Header language={language} setLanguage={setLanguage} />

      {/* Hero Section */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1608208291890-dcaf52dc98e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxsaXNib24lMjBjaXR5c2NhcGUlMjBnb2xkZW4lMjBob3VyfGVufDB8fHx8MTc4MDg2NTEzNnww&ixlib=rb-4.1.0&q=85"
          alt="Lisbon golden hour"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/90 via-[#0F172A]/65 to-[#0F172A]/40"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 w-full py-28 lg:py-0">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Pitch */}
            <div className="text-white fade-in-up">
              <p className="eyebrow text-[#D4AF37] mb-5">{t('Premium Transfers & Private Tours · Lisbon', 'Transfers & Tours Privados · Lisboa')}</p>
              <h1 className="heading-font text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight mb-6">
                {t('Lisbon Transfers & Private Tours,', 'Transfers & Tours Privados em Lisboa,')}
                <br />
                <span className="text-[#D4AF37]">{t('Elevated.', 'Elevados.')}</span>
              </h1>
              <p className="text-lg text-gray-200 mb-8 font-light leading-relaxed max-w-xl">
                {t(
                  'Get a free, no-obligation quote in seconds. Reliable airport transfers and unforgettable private tours across Lisbon, Sintra & Cascais.',
                  'Receba um orçamento grátis e sem compromisso em segundos. Transferências de aeroporto fiáveis e tours privados inesquecíveis por Lisboa, Sintra e Cascais.'
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => scrollTo('tours')}
                  data-testid="hero-tours-button"
                  className="border border-white/60 bg-white/5 text-white hover:bg-white hover:text-[#0F172A] rounded-sm px-8 py-6 text-base transition-colors"
                >
                  {t('Explore Private Tours', 'Ver Tours Privados')}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-3 gap-6 max-w-lg">
                {[
                  { icon: <Shield className="w-5 h-5" />, top: t('Licensed', 'Licenciado'), bottom: t('Certified', 'Certificado') },
                  { icon: <Clock className="w-5 h-5" />, top: '24/7', bottom: t('Available', 'Disponível') },
                  { icon: <Award className="w-5 h-5" />, top: '15+', bottom: t('Years', 'Anos') }
                ].map((item) => (
                  <div key={item.top} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-white/10 border border-white/15 flex items-center justify-center text-[#D4AF37]">
                      {item.icon}
                    </div>
                    <div>
                      <p className="font-semibold text-white text-sm">{item.top}</p>
                      <p className="text-xs text-gray-300">{item.bottom}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Quick Quote Form (above the fold) */}
            <div id="quote" className="fade-in-up">
              <QuickQuote language={language} />
            </div>
          </div>
        </div>
      </section>

      {/* Service Tiers Section */}
      <section id="tiers" className="py-24 bg-[#FBF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow text-[#D4AF37] mb-3">{t('Choose Your Tier', 'Escolha o Seu Escalão')}</p>
            <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F172A] mb-4">
              {t('Airport ⇄ City Transfers', 'Transferências Aeroporto ⇄ Cidade')}
            </h2>
            <p className="text-base text-[#475569] font-light leading-relaxed">
              {t('Two ways to travel — both with fixed prices and zero surprises.', 'Duas formas de viajar — ambas com preços fixos e zero surpresas.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {serviceTiers.map((tier) => (
              <Card
                key={tier.id}
                data-testid={`tier-card-${tier.id}`}
                className={`relative rounded-sm transition-all duration-300 hover:-translate-y-1 ${
                  tier.featured
                    ? 'border-2 border-[#D4AF37] shadow-lg bg-white'
                    : 'border border-[#E2E8F0] shadow-sm bg-white hover:shadow-lg'
                }`}
              >
                {tier.featured && (
                  <div className="absolute -top-3 left-8 bg-[#D4AF37] text-white text-[11px] uppercase tracking-[0.2em] px-3 py-1 rounded-sm flex items-center gap-1">
                    <Crown className="w-3 h-3" /> {t('Most Popular', 'Mais Procurado')}
                  </div>
                )}
                <CardHeader className="pt-10">
                  <CardTitle className="heading-font text-2xl font-medium flex items-center gap-2">
                    {tier.featured && <Crown className="w-5 h-5 text-[#D4AF37]" />}
                    {language === 'en' ? tier.name : tier.namePt}
                  </CardTitle>
                  <CardDescription className="text-sm text-[#475569]">{language === 'en' ? tier.route : tier.routePt}</CardDescription>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="text-xs text-[#475569] uppercase tracking-widest">{t('From', 'Desde')}</span>
                    <span className="heading-font text-5xl font-semibold text-[#0F172A]">€{tier.price}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#475569] font-light mb-5 leading-relaxed">
                    {language === 'en' ? tier.description : tier.descriptionPt}
                  </p>
                  <ul className="space-y-3 mb-7">
                    {(language === 'en' ? tier.features : tier.featuresPt).map((f) => (
                      <li key={f} className="flex items-center text-sm text-[#0F172A]">
                        <CheckCircle className="w-4 h-4 text-[#D4AF37] mr-3 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => scrollTo('booking')}
                    data-testid={`tier-book-${tier.id}`}
                    className={`w-full rounded-sm py-5 transition-colors ${
                      tier.featured
                        ? 'bg-[#D4AF37] hover:bg-[#B48E2D] text-white'
                        : 'bg-[#0F172A] hover:bg-[#1e293b] text-white'
                    }`}
                  >
                    {t('Book This Tier', 'Reservar Este Escalão')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow text-[#D4AF37] mb-3">{t('What We Offer', 'O Que Oferecemos')}</p>
            <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F172A] mb-4">
              {t('Our Services', 'Os Nossos Serviços')}
            </h2>
            <p className="text-base text-[#475569] font-light leading-relaxed">
              {t('Professional transportation and tour services tailored to your needs.', 'Serviços profissionais de transporte e tours adaptados às suas necessidades.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <Card key={service.id} className="group rounded-sm border border-[#E2E8F0] shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                  <div className="w-14 h-14 rounded-sm bg-[#FBF9F6] border border-[#E2E8F0] flex items-center justify-center mb-4 text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-white transition-colors duration-300">
                    {getIcon(service.icon)}
                  </div>
                  <CardTitle className="heading-font text-xl font-medium">{language === 'en' ? service.title : service.titlePt}</CardTitle>
                  <CardDescription className="text-sm font-light leading-relaxed">{language === 'en' ? service.description : service.descriptionPt}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center text-sm text-[#475569]">
                        <CheckCircle className="w-4 h-4 text-[#D4AF37] mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tours Section */}
      <section id="tours" className="py-24 bg-[#FBF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow text-[#D4AF37] mb-3">{t('Experiences', 'Experiências')}</p>
            <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F172A] mb-4">
              {t('Popular Tours', 'Tours Populares')}
            </h2>
            <p className="text-base text-[#475569] font-light leading-relaxed">
              {t('Experience the best of Lisbon and its surroundings.', 'Experimente o melhor de Lisboa e arredores.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tours.map((tour) => (
              <Card key={tour.id} data-testid={`tour-card-${tour.id}`} className="overflow-hidden group rounded-sm border border-[#E2E8F0] shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="relative h-60 overflow-hidden">
                  <img src={tour.image} alt={language === 'en' ? tour.name : tour.namePt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-4 right-4 bg-white px-4 py-1.5 rounded-sm shadow-sm">
                    <span className="text-xs text-[#475569] mr-1">{t('From', 'Desde')}</span>
                    <span className="font-semibold text-[#D4AF37]">{tour.price}</span>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="heading-font text-2xl font-medium">{language === 'en' ? tour.name : tour.namePt}</CardTitle>
                  <div className="flex items-center text-[#475569] text-sm mt-1 mb-2">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>{language === 'en' ? tour.duration : tour.durationPt}</span>
                  </div>
                  <CardDescription className="text-sm font-light leading-relaxed">{language === 'en' ? tour.description : tour.descriptionPt}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => scrollTo('booking')} className="w-full bg-[#0F172A] hover:bg-[#1e293b] text-white rounded-sm transition-colors">
                    {t('Book This Tour', 'Reservar Tour')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Long-Distance Transfers Section */}
      <section id="longtrips" className="py-24 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow text-[#D4AF37] mb-3">{t('We Go The Distance', 'Vamos Mais Longe')}</p>
            <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F172A] mb-4">
              {t('Long-Distance Transfers & Day Trips', 'Viagens Longas & Day Trips')}
            </h2>
            <p className="text-base text-[#475569] font-light leading-relaxed">
              {t('We also drive you across Portugal and Spain — Algarve, Porto, Seville and beyond, in full comfort.', 'Também o levamos por Portugal e Espanha — Algarve, Porto, Sevilha e mais além, com todo o conforto.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {longTrips.map((trip) => (
              <Card key={trip.id} data-testid={`longtrip-card-${trip.id}`} className="group rounded-sm border border-[#E2E8F0] shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col">
                <CardHeader>
                  <div className="w-12 h-12 rounded-sm bg-[#FBF9F6] border border-[#E2E8F0] flex items-center justify-center mb-4 text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-white transition-colors duration-300">
                    {trip.id === 4 ? <MapPin className="w-6 h-6" /> : <Route className="w-6 h-6" />}
                  </div>
                  <CardTitle className="heading-font text-xl font-medium leading-snug">{language === 'en' ? trip.route : trip.routePt}</CardTitle>
                  <div className="flex items-center text-[#475569] text-sm mt-1">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>{trip.duration === 'Custom' ? t('Custom route', 'Rota à medida') : trip.duration}</span>
                  </div>
                  <CardDescription className="text-sm font-light leading-relaxed mt-2">{language === 'en' ? trip.description : trip.descriptionPt}</CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <Button onClick={() => scrollTo('quote')} className="w-full bg-[#0F172A] hover:bg-[#1e293b] text-white rounded-sm transition-colors">
                    {t('Request a Quote', 'Pedir Orçamento')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section id="fleet" className="py-24 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow text-[#D4AF37] mb-3">{t('Travel in Comfort', 'Viaje com Conforto')}</p>
            <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F172A] mb-4">
              {t('Our Fleet', 'A Nossa Frota')}
            </h2>
            <p className="text-base text-[#475569] font-light leading-relaxed">
              {t('Modern, comfortable vehicles for every group size.', 'Veículos modernos e confortáveis para todos os tamanhos de grupo.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {vehicles.map((vehicle) => (
              <Card key={vehicle.id} className="overflow-hidden rounded-sm border border-[#E2E8F0] shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="relative h-56 overflow-hidden">
                  <img src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover" />
                </div>
                <CardHeader>
                  <CardTitle className="heading-font text-2xl font-medium">{vehicle.name}</CardTitle>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className="inline-flex items-center text-xs bg-[#FBF9F6] border border-[#E2E8F0] rounded-sm px-2.5 py-1 text-[#475569]">
                      <Users className="w-3 h-3 mr-1.5" />{language === 'en' ? vehicle.capacity : vehicle.capacityPt}
                    </span>
                    <span className="inline-flex items-center text-xs bg-[#FBF9F6] border border-[#E2E8F0] rounded-sm px-2.5 py-1 text-[#475569]">
                      {language === 'en' ? vehicle.luggage : vehicle.luggagePt}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {vehicle.features.map((feature) => (
                      <li key={feature} className="flex items-center text-sm text-[#475569]">
                        <CheckCircle className="w-4 h-4 text-[#D4AF37] mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-[#FBF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow text-[#D4AF37] mb-3">{t('Transparent Pricing', 'Preços Transparentes')}</p>
            <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F172A] mb-4">
              {t('Fixed Prices You Can Trust', 'Preços Fixos em Que Pode Confiar')}
            </h2>
            <p className="text-base text-[#475569] font-light leading-relaxed">
              {t('No hidden fees. The price you see is the price you pay.', 'Sem taxas ocultas. O preço que vê é o preço que paga.')}
            </p>
          </div>

          <Card className="overflow-hidden rounded-sm border border-[#E2E8F0] shadow-sm max-w-5xl">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0F172A] text-white">
                    <th className="px-6 py-4 text-left font-medium uppercase tracking-wider text-xs">{t('Route', 'Rota')}</th>
                    <th className="px-6 py-4 text-center font-medium uppercase tracking-wider text-xs">Low Cost</th>
                    <th className="px-6 py-4 text-center font-medium uppercase tracking-wider text-xs">Luxury</th>
                    <th className="px-6 py-4 text-center font-medium uppercase tracking-wider text-xs">Van</th>
                  </tr>
                </thead>
                <tbody>
                  {pricing.map((item, index) => (
                    <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-[#FBF9F6]'}>
                      <td className="px-6 py-4 font-medium text-[#0F172A]">{language === 'en' ? item.from : item.fromPt}</td>
                      <td className="px-6 py-4 text-center text-[#0F172A]">{item.lowcost}</td>
                      <td className="px-6 py-4 text-center text-[#0F172A]">{item.luxury}</td>
                      <td className="px-6 py-4 text-center text-[#0F172A]">{item.van}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <p className="text-sm text-[#475569] mt-5 font-light">
            {t('* Prices include all taxes and fees. No hidden charges.', '* Preços incluem todos os impostos e taxas. Sem cobranças ocultas.')}
          </p>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-16 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="eyebrow text-[#D4AF37] mb-6">{t('We Accept All Payment Methods', 'Aceitamos Todos os Métodos de Pagamento')}</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {paymentMethods.map((pm) => (
              <div key={pm.id} data-testid={`payment-${pm.id}`} className="flex items-center gap-2 bg-[#FBF9F6] border border-[#E2E8F0] rounded-sm px-5 py-3">
                <span className="text-[#D4AF37]">{payIcon(pm.icon)}</span>
                <span className="text-sm font-medium text-[#0F172A]">{language === 'en' ? pm.label : pm.labelPt}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-[#FBF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow text-[#D4AF37] mb-3">{t('Testimonials', 'Testemunhos')}</p>
            <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F172A] mb-4">
              {t('What Our Clients Say', 'O Que Dizem os Nossos Clientes')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="rounded-sm border border-[#E2E8F0] shadow-sm hover:shadow-lg transition-shadow duration-300 bg-white">
                <CardHeader>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`${testimonial.id}-star-${i}`} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                    ))}
                  </div>
                  <CardDescription className="heading-font text-xl text-[#0F172A] italic leading-relaxed">
                    {`"${language === 'en' ? testimonial.text : testimonial.textPt}"`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold text-[#0F172A]">{testimonial.name}</p>
                  <p className="text-sm text-[#475569]">{testimonial.country}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-24 bg-white border-t border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="eyebrow text-[#D4AF37] mb-3">{t('Reserve Now', 'Reserve Já')}</p>
            <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F172A] mb-4">
              {t('Book Your Experience', 'Reserve a Sua Experiência')}
            </h2>
            <p className="text-base text-[#475569] font-light">
              {t('Fill out the form below and we\'ll get back to you shortly.', 'Preencha o formulário abaixo e entraremos em contacto em breve.')}
            </p>
          </div>

          <Card className="rounded-sm border border-[#E2E8F0] shadow-lg">
            <CardContent className="pt-8">
              <BookingForm language={language} />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-[#FBF9F6]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p className="eyebrow text-[#D4AF37] mb-3">{t('Get In Touch', 'Fale Connosco')}</p>
          <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F172A] mb-12">
            {t('Have Questions? We\'re Here to Help', 'Tem Dúvidas? Estamos Aqui Para Ajudar')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="rounded-sm border border-[#E2E8F0] shadow-sm hover:shadow-lg transition-shadow duration-300 bg-white">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-14 h-14 rounded-sm bg-[#FBF9F6] border border-[#E2E8F0] flex items-center justify-center mx-auto mb-4 text-[#D4AF37]">
                  <Phone className="w-6 h-6" />
                </div>
                <h3 className="heading-font text-lg font-medium mb-2">{t('Call Us', 'Ligue-nos')}</h3>
                <a href={`tel:${company.phoneRaw}`} data-testid="contact-phone" className="text-[#D4AF37] hover:text-[#B48E2D] font-medium">{company.phone}</a>
                <p className="text-[#475569] mt-2 text-sm font-light">{t('Available 24/7', 'Disponível 24/7')}</p>
              </CardContent>
            </Card>

            <Card className="rounded-sm border border-[#E2E8F0] shadow-sm hover:shadow-lg transition-shadow duration-300 bg-white">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-14 h-14 rounded-sm bg-[#FBF9F6] border border-[#E2E8F0] flex items-center justify-center mx-auto mb-4 text-[#25D366]">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <h3 className="heading-font text-lg font-medium mb-2">WhatsApp</h3>
                <a href={`https://wa.me/${company.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="text-[#D4AF37] hover:text-[#B48E2D] font-medium">{company.phone}</a>
                <p className="text-[#475569] mt-2 text-sm font-light">{t('Chat with us instantly', 'Fale connosco já')}</p>
              </CardContent>
            </Card>

            <Card className="rounded-sm border border-[#E2E8F0] shadow-sm hover:shadow-lg transition-shadow duration-300 bg-white">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-14 h-14 rounded-sm bg-[#FBF9F6] border border-[#E2E8F0] flex items-center justify-center mx-auto mb-4 text-[#D4AF37]">
                  <Mail className="w-6 h-6" />
                </div>
                <h3 className="heading-font text-lg font-medium mb-2">{t('Email Us', 'Envie Email')}</h3>
                <a href={`mailto:${company.email}`} data-testid="contact-email" className="text-[#D4AF37] hover:text-[#B48E2D] font-medium break-all">{company.email}</a>
                <p className="text-[#475569] mt-2 text-sm font-light">{t('Quick response time', 'Resposta rápida')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partnerships Section */}
      <section id="partners" className="py-24 bg-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="eyebrow text-[#D4AF37] mb-3">{t('Partnerships', 'Parcerias')}</p>
              <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-6">
                {t('Partner With Us — Worldwide', 'Trabalhe Connosco — Mundialmente')}
              </h2>
              <p className="text-base text-gray-300 font-light leading-relaxed mb-5">
                {t(
                  'We collaborate with travel agencies, hotels, concierge services, tour operators and booking platforms across the globe. Offer your clients premium, reliable transfers and private tours in Lisbon — with competitive B2B rates, dedicated support and seamless coordination.',
                  'Colaboramos com agências de viagens, hotéis, serviços de concierge, operadores turísticos e plataformas de reservas em todo o mundo. Ofereça aos seus clientes transferências premium e tours privados em Lisboa — com tarifas B2B competitivas, apoio dedicado e coordenação perfeita.'
                )}
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  t('Competitive B2B / commission rates', 'Tarifas B2B / comissões competitivas'),
                  t('Dedicated partner support, 24/7', 'Apoio dedicado a parceiros, 24/7'),
                  t('Reliable service your clients will love', 'Serviço fiável que os seus clientes vão adorar')
                ].map((item) => (
                  <li key={item} className="flex items-center text-sm text-gray-200">
                    <CheckCircle className="w-4 h-4 text-[#D4AF37] mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                asChild
                data-testid="partner-cta"
                className="bg-[#D4AF37] hover:bg-[#B48E2D] text-white rounded-sm px-8 py-6 text-base font-medium transition-colors"
              >
                <a href={`mailto:${company.email}?subject=${encodeURIComponent(t('Partnership Inquiry — Elite Gold Transfers', 'Proposta de Parceria — Elite Gold Transfers'))}`}>
                  <Handshake className="mr-2 w-5 h-5" />
                  {t('Become a Partner', 'Tornar-se Parceiro')}
                </a>
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: <Globe className="w-7 h-7" />, label: t('Global Platforms', 'Plataformas Globais') },
                { icon: <Users className="w-7 h-7" />, label: t('Travel Agencies', 'Agências de Viagens') },
                { icon: <Award className="w-7 h-7" />, label: t('Hotels & Concierge', 'Hotéis & Concierge') },
                { icon: <Compass className="w-7 h-7" />, label: t('Tour Operators', 'Operadores Turísticos') }
              ].map((item) => (
                <div key={item.label} className="bg-white/5 border border-white/10 rounded-sm p-6 hover:bg-white/10 transition-colors duration-300">
                  <div className="text-[#D4AF37] mb-3">{item.icon}</div>
                  <p className="text-sm font-medium text-white">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer language={language} />

      {/* Floating WhatsApp Button */}
      <a
        href={`https://wa.me/${company.whatsapp}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float"
        aria-label="WhatsApp"
        className="fixed bottom-24 right-6 z-50 w-14 h-14 bg-[#25D366] hover:bg-[#1ebe57] rounded-full flex items-center justify-center shadow-lg transition-colors duration-300"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </a>
    </div>
  );
};
